import { createSlice } from '@reduxjs/toolkit'
import { AxisAlertInfo } from 'plugin-alerts-dashboard-channels/channelAlerts/types/axis'

type State = {
    open: boolean
    axisInfo: Record<string, AxisAlertInfo>
    widgetCriticality: Record<number, AxisAlertInfo>
    selectChannel: boolean
    axisInfoRaw: Record<string, Record<string, AxisAlertInfo>>
    widgetInfoRaw: Record<number, Record<string, AxisAlertInfo>>
}

const initialState: State = {
    open: false,
    axisInfo: {},
    widgetCriticality: {},
    selectChannel: false,
    axisInfoRaw: {},
    widgetInfoRaw: {}
}

const criticalityOrder = ['critical', 'high', 'medium', 'low', 'info']

const reducers = {
    setAxisInfo: (state: State, { payload }: { payload: AxisAlertInfo }) => {
        const key = buildAxisKey(payload.widgetId, payload.attributesKeys?.[0]) // TODO check attributesKeys

        upsert(state.axisInfoRaw, payload, key)
        upsert(state.widgetInfoRaw, payload, payload.widgetId)

        state.axisInfo[key] = getAxisLastAlarmingInfo(state.axisInfoRaw, key)
        state.widgetCriticality[payload.widgetId] = getWidgetLastAlarmingInfo(state.widgetInfoRaw, payload.widgetId)
    },
    setSelectChannel: (state: State, { payload }: { payload: boolean }) => {
        state.selectChannel = payload
    },
    reset: () => initialState
}

const alertSlice = createSlice({
    name: 'alertSlice',
    initialState: initialState,
    reducers: reducers
})

export const alertSliceActions = alertSlice.actions

export default alertSlice.reducer

export const buildAxisKey = (widgetId: number, attributeKey: string): string => `${widgetId}-${attributeKey}`

const checkAxisStatus = (axis: AxisAlertInfo): boolean => {
    if (!axis || axis.normalized || (axis.acknowledged && (axis.resolved || axis.falseAlert))) return false

    return true
}

const upsert = (rawData: State['axisInfoRaw' | 'widgetInfoRaw'], info: AxisAlertInfo, key: number | string): void => {
    if (!rawData[key]) rawData[key] = {}

    rawData[key][info.alertUid] = info
}

const getAxisLastAlarmingInfo = (axisInfoRaw: State['axisInfoRaw'], key: string): AxisAlertInfo => {
    const widgetAxis = axisInfoRaw[key]
    if (!widgetAxis) return {} as AxisAlertInfo

    return getMostCritical(widgetAxis) ?? ({} as AxisAlertInfo)
}

const getWidgetLastAlarmingInfo = (rawData: State['widgetInfoRaw'], widgetId: number): AxisAlertInfo => {
    const widget = rawData[widgetId]
    if (!widget) return {} as AxisAlertInfo

    return getMostCritical(widget) ?? ({} as AxisAlertInfo)
}

const getMostCritical = (data: State['axisInfo']): AxisAlertInfo => {
    const values = Object.values(data)
    if (!values.length) return {} as AxisAlertInfo

    const alarmingInfos = values.sort((a, b) => b.timestamp - a.timestamp).filter(info => checkAxisStatus(info))

    const mostCritical = alarmingInfos.reduce((acc, info) => {
        const criticalityIndex = criticalityOrder.indexOf(info.criticality?.toLowerCase())
        const accCriticalityIndex = criticalityOrder.indexOf(acc.criticality?.toLowerCase())

        return criticalityIndex < accCriticalityIndex ? info : acc
    }, alarmingInfos[0])

    return mostCritical ?? ({} as AxisAlertInfo)
}
